import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../lib/supabase'
import { Plus, Trash2, ChevronUp, ChevronDown, Clock, Download, Save } from 'lucide-react'

const PRESETS = {
  'White Wedding': [
    { title:"Arrival of Guests", duration:30 },{ title:"Arrival of the Groom", duration:5 },
    { title:"Arrival of the Bride", duration:10 },{ title:"Opening Prayer", duration:5 },
    { title:"Worship & Praise", duration:20 },{ title:"Sermon / Minister's Address", duration:25 },
    { title:"Exchange of Vows", duration:10 },{ title:"Exchange of Rings", duration:5 },
    { title:"Signing of Register", duration:10 },{ title:"Pronouncement & First Kiss", duration:5 },
    { title:"Presentation of the Couple", duration:5 },{ title:"Closing Prayer", duration:5 },
  ],
  'Traditional / Engagement': [
    { title:"Arrival of Guests", duration:30 },{ title:"Introduction of Families", duration:15 },
    { title:"Opening Prayers", duration:10 },{ title:"Presentation of Bride", duration:15 },
    { title:"Bride Price Negotiation", duration:20 },{ title:"Kola Nut Ceremony", duration:10 },
    { title:"Exchange of Gifts", duration:20 },{ title:"Blessing of the Couple", duration:10 },
    { title:"Cutting of Cake", duration:5 },{ title:"Dance Session", duration:60 },
    { title:"Closing Prayers", duration:10 },
  ],
  'Reception': [
    { title:"Cocktail Hour / Guest Arrival", duration:60 },{ title:"Grand Entrance of Couple", duration:10 },
    { title:"Opening Prayer", duration:5 },{ title:"Welcome Address", duration:10 },
    { title:"Dinner Service", duration:60 },{ title:"Toast & Speeches", duration:20 },
    { title:"Cutting of the Cake", duration:10 },{ title:"First Dance", duration:5 },
    { title:"Open Dance Floor", duration:90 },{ title:"Closing Prayer & Benediction", duration:5 },
  ],
  "Bridal Shower": [
    { title:"Guest Arrival & Welcome Drinks", duration:30 },{ title:"Opening / Welcome Address", duration:10 },
    { title:"Games & Activities", duration:45 },{ title:"Gift Opening Ceremony", duration:30 },
    { title:"Brunch / Lunch Service", duration:45 },{ title:"Cake & Desserts", duration:15 },
    { title:"Photo Session", duration:20 },{ title:"Closing & Departure", duration:15 },
  ],
  "Bachelor's Eve": [
    { title:"Guest Arrival", duration:30 },{ title:"Welcome Toast", duration:10 },
    { title:"Dinner Service", duration:60 },{ title:"Games & Challenges", duration:45 },
    { title:"Speeches & Toasts", duration:20 },{ title:"Night Activities", duration:90 },
  ],
  "Introduction": [
    { title:"Arrival of Bride's Family", duration:20 },{ title:"Arrival of Groom's Family", duration:20 },
    { title:"Opening Prayer", duration:10 },{ title:"Introduction of Both Families", duration:30 },
    { title:"Statement of Purpose", duration:15 },{ title:"Presentation of Bride", duration:15 },
    { title:"Acceptance & Celebration", duration:20 },{ title:"Sharing of Kola / Refreshments", duration:20 },
    { title:"Closing Prayer", duration:10 },
  ],
  "Thanksgiving": [
    { title:"Arrival of Guests", duration:30 },{ title:"Praise & Worship", duration:30 },
    { title:"Opening Prayer", duration:10 },{ title:"Thanksgiving Songs", duration:20 },
    { title:"Bible Reading & Sermon", duration:30 },{ title:"Couple's Testimony", duration:10 },
    { title:"Special Offering", duration:10 },{ title:"Benediction", duration:5 },
  ],
}
const DEFAULT_PRESET = [
  { title:"Guest Arrival", duration:30 },{ title:"Opening", duration:10 },
  { title:"Main Programme", duration:60 },{ title:"Refreshments", duration:30 },
  { title:"Closing", duration:10 },
]

function timePlus(base, mins) {
  if (!base) return ''
  const [h,m] = base.split(':').map(Number)
  const total = h*60+m+mins
  return `${String(Math.floor(total/60)%24).padStart(2,'0')}:${String(total%60).padStart(2,'0')}`
}

export default function ProgrammeTab({ event, weddingId }) {
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(true)
  const [startTime, setStartTime] = useState('09:00')
  const [newTitle, setNewTitle]   = useState('')
  const [newDur, setNewDur]       = useState(10)
  const [newDesc, setNewDesc]     = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)
  const [published, setPublished] = useState(false)
  const [addError, setAddError] = useState('')

  const fetchItems = useCallback(async () => {
    if (!event?.id) return
    setLoading(true)
    const { data } = await supabase.from('programme_items').select('*')
      .eq('event_id',event.id).order('sort_order')
    const list = data||[]
    setItems(list)
    setPublished(list.some(i=>i.published))
    setLoading(false)
  },[event?.id])

  useEffect(()=>{ fetchItems() },[fetchItems])

  async function addItem() {
    if (!newTitle.trim()) return
    setAddError('')
    if (!weddingId) { setAddError('Wedding not loaded — please refresh the page.'); return }
    if (!event?.id) { setAddError('Event not found.'); return }
    const { data, error } = await supabase.from('programme_items').insert({
      event_id: event.id,
      wedding_id: weddingId,
      title: newTitle.trim(),
      duration_mins: Number(newDur)||10,
      description: newDesc||null,
      sort_order: items.length,
      position: items.length,
      published: false,
    }).select().single()
    if (error) { setAddError('Failed to add: ' + error.message); return }
    if (data) {
      setItems(prev=>[...prev,data])
      setNewTitle(''); setNewDur(10); setNewDesc('')
    }
  }

  async function deleteItem(id) {
    await supabase.from('programme_items').delete().eq('id',id)
    setItems(prev=>prev.filter(i=>i.id!==id))
  }

  async function moveItem(idx, dir) {
    const arr = [...items]
    const swap = idx+dir
    if (swap<0||swap>=arr.length) return
    ;[arr[idx],arr[swap]] = [arr[swap],arr[idx]]
    setItems(arr)
    await Promise.all(arr.map((item,i)=>supabase.from('programme_items').update({sort_order:i}).eq('id',item.id)))
  }

  async function loadPreset() {
    if (!weddingId) { setAddError('Wedding not loaded — please refresh the page.'); return }
    setAddError('')
    const preset = PRESETS[event?.type] || DEFAULT_PRESET
    await supabase.from('programme_items').delete().eq('event_id',event.id)
    const { data, error } = await supabase.from('programme_items').insert(
      preset.map((p,i)=>({ event_id:event.id, wedding_id:weddingId, title:p.title, duration_mins:p.duration, sort_order:i, position:i, published:false }))
    ).select()
    if (error) { setAddError('Template load failed: ' + error.message); return }
    setItems(data||[])
    setPublished(false)
  }

  async function saveAndPublish() {
    setSaving(true)
    await supabase.from('programme_items').update({ published:true }).eq('event_id',event.id)
    setItems(prev=>prev.map(i=>({...i,published:true})))
    setPublished(true)
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),3000)
  }

  async function saveTitle(id) {
    if (!editTitle.trim()) return
    await supabase.from('programme_items').update({ title:editTitle }).eq('id',id)
    setItems(prev=>prev.map(i=>i.id===id?{...i,title:editTitle}:i))
    setEditingId(null); setEditTitle('')
  }

  function downloadPDF() {
    const evLabel = event?.type||'Wedding'
    const evDate  = event?.date ? new Date(event.date).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'}) : ''
    const evVenue = event?.venue||''
    const rows = timed.map(({item,time})=>`
      <tr>
        <td style="color:#C4826A;font-weight:700;white-space:nowrap;padding:8px 12px 8px 0;vertical-align:top">${time}</td>
        <td style="padding:8px 0;border-bottom:1px solid #F3F0EA;vertical-align:top">
          <div style="font-size:0.9rem;font-weight:600;color:#1A1A1A">${item.title}</div>
          <div style="font-size:0.75rem;color:#BCBCBC">${item.duration_mins} min${item.description?' · '+item.description:''}</div>
        </td>
      </tr>`).join('')
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Programme</title>
<style>*{box-sizing:border-box}body{font-family:Georgia,serif;max-width:580px;margin:40px auto;color:#1A1A1A;padding:0 24px}h1{font-size:1.6rem;font-weight:400;margin:0 0 4px;color:#1A1A1A}h2{font-size:0.95rem;font-weight:400;color:#C4826A;margin:0 0 4px}p{margin:0 0 24px;color:#6B6B6B;font-size:0.82rem}table{width:100%;border-collapse:collapse}.footer{text-align:center;margin-top:2rem;font-size:0.7rem;color:#BCBCBC}</style>
</head><body>
<h1>Order of Service</h1>
<h2>${evLabel}</h2>
<p>${evDate}${evVenue?' · '+evVenue:''}</p>
<table>${rows}</table>
<p class="footer">Made with Okoiya</p>
</body></html>`
    const blob = new Blob([html],{type:'text/html;charset=utf-8'})
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url; a.download = `programme-${evLabel.replace(/\s+/g,'-').toLowerCase()}.html`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    setTimeout(()=>URL.revokeObjectURL(url), 1000)
  }

  function calcTimes(start) {
    let cur = start
    return items.map(item=>{ const time=cur; cur=timePlus(cur,item.duration_mins||0); return {item,time} })
  }

  const timed = calcTimes(startTime)
  const totalMins = items.reduce((s,i)=>s+(i.duration_mins||0),0)
  const hrs = Math.floor(totalMins/60), mins = totalMins%60
  const hasPreset = !!(PRESETS[event?.type])

  if (loading) return <p style={{fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',padding:'1rem',fontSize:'0.82rem'}}>Loading…</p>

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'1.125rem'}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:'0.75rem'}}>
        <div>
          <h2 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',margin:'0 0 0.2rem'}}>Programme — {event?.type}</h2>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#6B6B6B',margin:0}}>
            {items.length} items · {hrs>0?`${hrs}h `:''}{mins}min total
            {published&&<span style={{color:'#9BAF9B',fontWeight:700,marginLeft:'0.5rem'}}>✓ Live on guest portal</span>}
          </p>
        </div>
        <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
          {items.length>0&&<button onClick={downloadPDF} className="btn-secondary" style={{display:'flex',alignItems:'center',gap:'0.35rem',fontSize:'0.78rem',padding:'0.5rem 0.875rem'}}>
            <Download size={13}/> PDF
          </button>}
          <button onClick={saveAndPublish} disabled={saving||items.length===0} className="btn-primary" style={{display:'flex',alignItems:'center',gap:'0.35rem',fontSize:'0.78rem',padding:'0.5rem 0.875rem',background:saved?'#9BAF9B':undefined}}>
            <Save size={13}/> {saved?'Published!':saving?'Saving…':'Save & Publish'}
          </button>
        </div>
      </div>

      {/* Controls row */}
      <div style={{display:'flex',alignItems:'center',gap:'0.75rem',flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
          <Clock size={14} color="#6B6B6B"/>
          <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:0,whiteSpace:'nowrap'}}>Starts at</label>
          <input type="time" value={startTime} onChange={e=>setStartTime(e.target.value)}
            style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',padding:'0.35rem 0.625rem',border:'1.5px solid #E5E0D8',borderRadius:'0.5rem',outline:'none'}}/>
        </div>
        <button onClick={loadPreset} className="btn-secondary" style={{fontSize:'0.75rem',padding:'0.4rem 0.875rem'}}>
          {hasPreset?`Load ${event?.type} Template`:'Load Template'}
        </button>
      </div>

      {/* Items */}
      {items.length===0?(
        <div style={{textAlign:'center',padding:'2rem',background:'#FAFAF8',borderRadius:'0.875rem',border:'1px dashed #E5E0D8'}}>
          <p style={{fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',fontSize:'0.82rem',margin:0}}>No items yet. Add items below or load a template.</p>
        </div>
      ):(
        <div style={{display:'flex',flexDirection:'column',gap:'0.4rem'}}>
          {timed.map(({item,time},idx)=>(
            <div key={item.id} style={{background:'white',border:'1px solid #E5E0D8',borderRadius:'0.75rem',padding:'0.625rem 0.875rem',display:'flex',alignItems:'center',gap:'0.5rem',flexWrap:'wrap'}}>
              <div style={{display:'flex',flexDirection:'column',gap:'1px',flexShrink:0}}>
                <button onClick={()=>moveItem(idx,-1)} disabled={idx===0} style={{background:'none',border:'none',cursor:'pointer',color:'#BCBCBC',padding:'1px',lineHeight:1}}><ChevronUp size={12}/></button>
                <button onClick={()=>moveItem(idx,1)} disabled={idx===items.length-1} style={{background:'none',border:'none',cursor:'pointer',color:'#BCBCBC',padding:'1px',lineHeight:1}}><ChevronDown size={12}/></button>
              </div>
              <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#C4826A',fontWeight:700,flexShrink:0,minWidth:40}}>{time}</span>
              <div style={{flex:1,minWidth:80}}>
                {editingId===item.id?(
                  <input value={editTitle} onChange={e=>setEditTitle(e.target.value)} autoFocus
                    onBlur={()=>saveTitle(item.id)} onKeyDown={e=>{if(e.key==='Enter')saveTitle(item.id);if(e.key==='Escape'){setEditingId(null);setEditTitle('')}}}
                    style={{width:'100%',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',padding:'0.25rem 0.5rem',border:'1.5px solid #C4826A',borderRadius:'0.375rem',outline:'none'}}/>
                ):(
                  <p onClick={()=>{setEditingId(item.id);setEditTitle(item.title)}} style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.85rem',color:'#1A1A1A',margin:0,cursor:'text'}}>{item.title}</p>
                )}
                {item.description&&<p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#6B6B6B',margin:'1px 0 0'}}>{item.description}</p>}
              </div>
              <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#BCBCBC',flexShrink:0}}>{item.duration_mins}min</span>
              <button onClick={()=>deleteItem(item.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#BCBCBC',padding:'2px',flexShrink:0}}><Trash2 size={13}/></button>
            </div>
          ))}
        </div>
      )}

      {/* Add item form */}
      <div style={{background:'#FAFAF8',border:'1px solid #E5E0D8',borderRadius:'0.875rem',padding:'1rem'}}>
        <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.78rem',color:'#1A1A1A',margin:'0 0 0.75rem'}}>+ Add Programme Item</p>
        <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
          <div>
            <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#6B6B6B',fontWeight:600,display:'block',marginBottom:'0.25rem'}}>Item Title *</label>
            <input className="input-field" placeholder="e.g. First Dance, Prayer, Toast…" value={newTitle} onChange={e=>setNewTitle(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&addItem()}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:'0.5rem'}}>
            <div>
              <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#6B6B6B',fontWeight:600,display:'block',marginBottom:'0.25rem'}}>Duration (minutes)</label>
              <input className="input-field" type="number" min="1" placeholder="10" value={newDur} onChange={e=>setNewDur(e.target.value)}/>
            </div>
            <div>
              <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#6B6B6B',fontWeight:600,display:'block',marginBottom:'0.25rem'}}>Description (optional)</label>
              <input className="input-field" placeholder="Extra details…" value={newDesc} onChange={e=>setNewDesc(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&addItem()}/>
            </div>
          </div>
          <button onClick={addItem} disabled={!newTitle.trim()} className="btn-primary"
            style={{display:'flex',alignItems:'center',gap:'0.4rem',justifyContent:'center',width:'100%',padding:'0.75rem',opacity:!newTitle.trim()?0.5:1}}>
            <Plus size={15}/> Add Item
          </button>
        </div>
      </div>

      {addError&&(
        <div style={{background:'rgba(196,130,106,0.08)',border:'1px solid rgba(196,130,106,0.3)',borderRadius:'0.75rem',padding:'0.75rem 1rem'}}>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#C4826A',margin:0,fontWeight:600}}>⚠ {addError}</p>
        </div>
      )}
      {!published&&items.length>0&&(
        <div style={{background:'rgba(196,130,106,0.06)',border:'1px solid rgba(196,130,106,0.2)',borderRadius:'0.75rem',padding:'0.75rem 1rem'}}>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#6B6B6B',margin:0}}>
            📌 Tap <strong>Save & Publish</strong> to make this programme visible to guests on your wedding portal.
          </p>
        </div>
      )}
    </div>
  )
}
